import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDownRight, ArrowUpRight, Boxes, CalendarClock, Sparkles, TrendingUp } from 'lucide-react';
import { formatMoney, formatShopDate } from '@bakery/shared';

import { api } from '../lib/api.js';
import { Badge, Button, Card, EmptyState, ErrorNote, SectionHeading, Skeleton, Tabs, cx } from '../components/ui.js';

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: '7 days' },
  { value: 'month', label: '30 days' },
  { value: 'quarter', label: '90 days' },
] as const;

export function DashboardScreen() {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]['value']>('today');

  const dashboard = useQuery({
    queryKey: ['analytics', 'dashboard', period],
    queryFn: () => api.analytics.dashboard(period),
    refetchInterval: 120_000,
  });

  const forecast = useQuery({ queryKey: ['analytics', 'forecast'], queryFn: () => api.analytics.forecast() });

  if (dashboard.isError) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-5">
        <ErrorNote error={dashboard.error} retry={() => dashboard.refetch()} />
      </div>
    );
  }

  const summary = dashboard.data?.summary;
  const previous = summary?.previous;

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Dashboard</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">How the shop is doing right now.</p>
        </div>
        <Tabs value={period} onChange={setPeriod} tabs={PERIODS.map((p) => ({ value: p.value, label: p.label }))} />
      </header>

      {/* Headline numbers -------------------------------------------------- */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {dashboard.isLoading
          ? Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-24" />)
          : summary && (
              <>
                <StatTile label="Net sales" value={formatMoney(summary.net)} delta={delta(summary.net, previous?.net)} />
                <StatTile label="Profit" value={formatMoney(summary.profit)} sub={typeof summary.marginPercent === 'number' ? `${summary.marginPercent}% margin` : undefined} delta={delta(summary.profit, previous?.profit)} />
                <StatTile label="Orders" value={String(summary.orderCount)} sub={`${formatMoney(summary.averageOrderValue)} average`} delta={delta(summary.orderCount, previous?.orderCount)} />
                <StatTile label="Items sold" value={String(summary.itemsSold)} sub={`${summary.newCustomers} new client${summary.newCustomers === 1 ? '' : 's'}`} />
              </>
            )}
      </div>

      {/* Sales trend -------------------------------------------------------- */}
      <Card className="p-4">
        <SectionHeading title="Last 30 days" hint="Net sales, with ingredient cost taken off to show profit." />
        {dashboard.isLoading ? (
          <Skeleton className="h-56" />
        ) : (dashboard.data?.series.length ?? 0) < 2 ? (
          <EmptyState
            icon={<TrendingUp className="size-8" />}
            title="Not enough history yet"
            description="The trend line fills in once you have a few days of sales."
          />
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dashboard.data?.series} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-crust-500)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--color-crust-500)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--color-line)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  tickFormatter={(value: string) => formatShopDate(`${value}T00:00:00Z`, 'UTC', { day: 'numeric', month: 'short' })}
                  tick={{ fontSize: 11, fill: 'var(--color-ink-faint)' }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                />
                <YAxis
                  tickFormatter={(value: number) => `₱${Math.round(value / 100)}`}
                  tick={{ fontSize: 11, fill: 'var(--color-ink-faint)' }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-surface-raised)',
                    border: '1px solid var(--color-line)',
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  labelFormatter={(value) => formatShopDate(`${value}T00:00:00Z`, 'UTC')}
                  formatter={(value: number, name) => [formatMoney(value), name === 'net' ? 'Net sales' : 'Profit']}
                />
                <Area type="monotone" dataKey="net" stroke="var(--color-crust-600)" strokeWidth={2} fill="url(#netFill)" />
                <Area type="monotone" dataKey="profit" stroke="var(--color-sage-700)" strokeWidth={1.5} fill="none" strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Bake forecast --------------------------------------------------- */}
        <Card className="p-4">
          <SectionHeading
            title="What to bake"
            hint="Suggested quantities for tomorrow, from your own sales pattern."
            action={
              <Link to="/inventory" className="text-xs font-medium text-crust-600 hover:underline">
                Stock room
              </Link>
            }
          />

          {forecast.data?.warnings.map((warning) => (
            <p key={warning} className="mb-3 rounded-[var(--radius-md)] bg-[var(--color-warn-soft)] px-3 py-2 text-xs text-[var(--color-warn)]">
              {warning}
            </p>
          ))}

          {(() => {
            const tomorrow = forecast.data?.points.filter((point) => point.date === forecast.data?.points[0]?.date) ?? [];
            if (tomorrow.length === 0) {
              return (
                <EmptyState
                  icon={<Sparkles className="size-8" />}
                  title="No forecast yet"
                  description="Ring up a few days of sales and this fills in on its own."
                />
              );
            }
            return (
              <ul className="space-y-2">
                {tomorrow.slice(0, 6).map((point) => (
                  <li key={point.productId} className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate text-sm">{point.productName}</span>
                    {point.committedQty > 0 && <Badge tone="info">{point.committedQty} ordered</Badge>}
                    <span className="tabular text-sm font-semibold">{point.suggestedBakeQty}</span>
                    <span
                      title={`Confidence ${Math.round(point.confidence * 100)}%`}
                      className={cx(
                        'size-1.5 shrink-0 rounded-full',
                        point.confidence > 0.6 ? 'bg-[var(--color-ok)]' : point.confidence > 0.3 ? 'bg-[var(--color-warn)]' : 'bg-[var(--color-line-strong)]',
                      )}
                    />
                  </li>
                ))}
              </ul>
            );
          })()}
        </Card>

        {/* Stock alerts ---------------------------------------------------- */}
        <Card className="p-4">
          <SectionHeading title="Running low" hint="Days of cover at your recent rate of use." />
          {dashboard.data?.stockAlerts.length === 0 ? (
            <EmptyState icon={<Boxes className="size-8" />} title="Everything is stocked" description="No ingredient is near its reorder point." />
          ) : (
            <ul className="space-y-2">
              {dashboard.data?.stockAlerts.map((alert) => (
                <li key={alert.ingredientId} className="flex items-center gap-3">
                  <Badge tone={alert.level === 'out' || alert.level === 'critical' ? 'danger' : 'warn'}>
                    {alert.level === 'out' ? 'Out' : alert.daysOfCover !== null ? `${alert.daysOfCover}d` : 'Low'}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate text-sm">{alert.ingredientName}</span>
                  <span className="tabular text-xs text-[var(--color-ink-soft)]">
                    {alert.qtyDisplay.toLocaleString('en-PH', { maximumFractionDigits: 2 })} {alert.stockUnit}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <SectionHeading title="Best sellers" hint="By sales value in this period." />
          {dashboard.data?.topProducts.length === 0 ? (
            <p className="py-4 text-sm text-[var(--color-ink-faint)]">No sales in this period.</p>
          ) : (
            <ul className="space-y-2.5">
              {dashboard.data?.topProducts.map((product) => (
                <li key={product.productId}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">{product.productName}</span>
                    <span className="tabular shrink-0 font-medium">{formatMoney(product.net)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-sunken)]">
                      <div className="h-full rounded-full bg-crust-500" style={{ width: `${product.salesShare}%` }} />
                    </div>
                    <span className="tabular w-16 shrink-0 text-right text-[11px] text-[var(--color-ink-faint)]">
                      {product.qtySold} sold
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <SectionHeading title="Orders on the board" hint="Open orders by when they are needed." />
          <ul className="space-y-2">
            {Object.entries(dashboard.data?.orderBuckets ?? {}).map(([bucket, count]) => (
              <li key={bucket}>
                <Link
                  to="/orders"
                  className="flex items-center justify-between rounded-[var(--radius-md)] px-3 py-2 text-sm hover:bg-[var(--color-surface-sunken)]"
                >
                  <span className="flex items-center gap-2">
                    <CalendarClock className="size-3.5 text-[var(--color-ink-faint)]" />
                    <span className="capitalize">{bucket}</span>
                  </span>
                  <span className={cx('tabular font-semibold', bucket === 'overdue' && count > 0 && 'text-[var(--color-danger)]')}>
                    {count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {(dashboard.data?.channels.length ?? 0) > 0 && (
            <>
              <h3 className="mt-4 mb-2 text-sm font-semibold">Where orders come from</h3>
              <ul className="space-y-1.5">
                {dashboard.data?.channels.map((channel) => (
                  <li key={channel.source} className="flex justify-between text-sm">
                    <span className="capitalize">{channel.source.replace('_', ' ')}</span>
                    <span className="tabular text-[var(--color-ink-soft)]">
                      {channel.orderCount} · {formatMoney(channel.net)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function delta(current: number, previous: number | undefined): number | null {
  if (previous === undefined || previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

function StatTile({
  label,
  value,
  sub,
  delta: change,
}: {
  label: string;
  value: string;
  sub?: string;
  delta?: number | null;
}) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium text-[var(--color-ink-soft)]">{label}</p>
      <p className="tabular mt-1 font-display text-2xl font-semibold">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {change !== null && change !== undefined && (
          <span
            className={cx(
              'tabular inline-flex items-center gap-0.5 text-xs font-medium',
              change >= 0 ? 'text-[var(--color-ok)]' : 'text-[var(--color-danger)]',
            )}
          >
            {change >= 0 ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
            {Math.abs(change)}%
          </span>
        )}
        {sub && <span className="truncate text-xs text-[var(--color-ink-faint)]">{sub}</span>}
      </div>
    </Card>
  );
}
